<template>
  <div
    v-if="userHasPermissions('provider.specialist.serviceplace.view')"
    v-show="!disabled || (disabled && this.locations.length > 0)"
  >
    <section-title
      content="Información de Lugar(es) de Servicio(s)"
      :has-line="true"
    />

    <div
      v-if="locations && locations.length > 0"
      class="serviceplace-information-list mt-3"
    >
      <template v-for="(location, idx) in sortedLocations">
        <callout
          v-if="(idx >= pageStartIndex) & (idx < pageEndIndex)"
          :key="location.specialistLocationId"
          class="mb-2"
          :has-toggle="false"
          :opened="!disabled && editMode"
        >
          <div class="row">
            <div class="col-sm-12 col-md cell sd">
              <div class="data">
                <span>Tipo de Lugar:</span>
                <p>{{ location.locationTypeName }}</p>
              </div>
            </div>
            <div class="col-sm-12 col-md cell">
              <div class="data d-inline-block">
                <span>Municipio:</span>
                <p>{{ location.municipality.name }}</p>
              </div>
            </div>
            <div class="col-sm-12 col-md cell">
              <div class="data">
                <span>{{ location.locationTypeName }}:</span>
                <p>{{ location.locationName }}</p>
              </div>
            </div>
            <div class="col-sm-12 col-md-1 col-lg-2 cell">
              <div
                v-if="location.canAssignSpecialistFlag"
                class="data d-inline-block"
              >
                <custom-balloon
                  background-color="gray"
                  class="mt-2 pl-2 pr-2 font-weight-normal font-16"
                  text="Admisión"
                />
              </div>
            </div>
            <div class="col-sm-12 col-md-1 col-lg-2 cell">
              <div v-if="location.selfAssign" class="data d-inline-block">
                <custom-balloon
                  background-color="gray"
                  class="mt-2 pl-2 pr-2 font-weight-normal font-16"
                  text="Asignar"
                />
              </div>
            </div>
            <div
              v-if="!disabled && canPerformActions"
              class="col-sm-12 col-md cell pr-5"
            >
              <drop-down
                class="float-right"
                :data-item="idx"
                :items="getAvailableActions()"
              />
            </div>
          </div>

          <template v-if="editMode && editIndex == idx" #edition>
            <service-place-information-add-edit
              v-model="sortedLocations[idx]"
              :company-id="specialist.companyId"
              :fiscal-year-id="specialist.fiscalYearId"
              :immediate-save="immediateSave"
              op-mode="E"
              :self-assign="selfAssign"
              :specialist-id="specialistId"
              :unsaved-license-data="specialist.licenses"
              @on-cancel="clearEditCreate"
              @on-saved="emitLocationChange"
            />
          </template>
        </callout>
      </template>
      <div v-if="!disabled" class="row mt-3">
        <div class="col">
          <action-button
            v-if="!editMode && !createMode && canAddServicePlace"
            class="float-right"
            i="fas fa-file-plus"
            variant="success"
            @click="onAdd"
          >
            Añadir
          </action-button>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <pagination
            v-if="this.pagingInfo.totalItemCount > 5"
            class="mb-3"
            :data="this.pagingInfo"
            :show-text="true"
            @pagination-go-page="setCurrentPage"
          />
        </div>
      </div>
    </div>
    <div v-else class="serviceplace-information-list mt-3">
      <callout
        v-if="
          (!locations || locations.length == 0) &&
            canAddServicePlace &&
            !createMode &&
            !editMode &&
            !disabled
        "
        class-main="py-3"
        :has-toggle="false"
      >
        <div class="row d-flex justify-content-center">
          <action-button
            class="border w-40 btn"
            i="fas fa-file-plus color-primary-1"
            text="Presione aquí para añadir Lugar(es) de Servicio(s)"
            @click="onAdd"
          />
        </div>
      </callout>
    </div>

    <callout
      class="mb-2"
      :opened="!disabled && createMode"
      :show-container="false"
    >
      <template #edition>
        <service-place-information-add-edit
          v-model="newLocation"
          :can-add-locations="canAddLocations"
          :company-id="specialist.companyId"
          :custom-error-message="addEditCustomErrorMessage"
          :fiscal-year-id="specialist.fiscalYearId"
          :immediate-save="immediateSave"
          :locations="locations"
          op-mode="I"
          :self-assign="selfAssign"
          :specialist-id="specialistId"
          :unsaved-license-data="specialist.licenses"
          @finish-add-location="$emit('finish-add-location')"
          @init-add-locations="$emit('init-add-locations')"
          @on-cancel="clearEditCreate"
          @on-saved="onLocationAdded"
        />
      </template>
    </callout>
  </div>
</template>

<script>
import SectionTitle from '@/shared/components/SectionTitle';
import Pagination from 'pagination';
import ServicePlaceInformationAddEdit from './ServicePlaceInformationAddEdit';
import CustomBalloon from '@/components/common/CustomBalloon';
import { deleteServicePlace } from '@/modules/provider/api';

import alert from '@/mixins/alert.mixin';
import { commonMixin } from '@/modules/common/mixins/common.mixin';

import { getMunicipalityNameByLocationFiscalSpecialist as _getMunicipalityNameByLocationFiscalSpecialist } from '@/modules/provider/api.js';

export default {
  name: 'ServicePlaceInformationList',
  components: {
    ServicePlaceInformationAddEdit,
    SectionTitle,
    Pagination,
    CustomBalloon,
  },
  mixins: [alert, commonMixin],
  props: {
    specialist: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    specialistId: {
      type: Number,
      default: () => null,
    },
    onDeleting: {
      type: Function,
      default: null,
    },
    addEnabled: {
      type: Boolean,
      default: true,
    },
    editEnabled: {
      type: Boolean,
      default: true,
    },
    deleteEnabled: {
      type: Boolean,
      default: true,
    },
    immediateSave: {
      type: Boolean,
      required: true,
    },
    selfAssign: {
      type: Boolean,
      required: false,
    },
    canAddLocations: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      specialistData: {},
      locations: [],
      editMode: false,
      editIndex: null,
      createMode: false,
      newLocation: {},
      pagingInfo: {
        totalItemCount: 0,
        count: 5,
        pageCount: 0,
        currentPage: 1,
      },
      addEditCustomErrorMessage: null,
    };
  },
  computed: {
    pageStartIndex() {
      return this.pagingInfo.count * (this.pagingInfo.currentPage - 1);
    },
    pageEndIndex() {
      return (
        this.pagingInfo.count * (this.pagingInfo.currentPage - 1) +
        this.pagingInfo.count
      );
    },
    canAddServicePlace() {
      return this.addEnabled;
    },
    canPerformActions() {
      return this.editEnabled || this.deleteEnabled;
    },

    sortedLocations() {
      return [...this.locations].sort(
        this.dynamicSortMultiple('locationTypeName', 'municipalityName')
      );
    },
  },
  watch: {
    specialist: {
      handler: function(newValue) {
        this.specialistData = newValue;
        this.locations = newValue.locations;

        // Refresh de UI when the page information changes (add/delete)
        let pageCount = Math.ceil(
          newValue.locations.length / this.pagingInfo.count
        );

        if (this.pagingInfo.currentPage <= pageCount) {
          this.setCurrentPage(this.pagingInfo.currentPage);
        } else {
          this.setCurrentPage(this.pagingInfo.currentPage - 1);
        }
      },
      deep: true,
    },
    createMode: function(newValue) {
      if (newValue) {
        this.$emit('on-opened');
      }
    },
    editMode: function(newValue) {
      if (newValue) {
        this.$emit('on-opened');
      }
    },
  },
  created() {
    if (!this.immediateSave) {
      this.specialistData = this.specialist;
    }
  },
  methods: {
    setCurrentPage(pageNumber) {
      pageNumber = pageNumber < 1 ? 1 : pageNumber;
      this.pagingInfo.currentPage = pageNumber;
      this.pagingInfo.totalItemCount = this.locations.length;
      this.pagingInfo.pageCount = Math.ceil(
        this.locations.length / this.pagingInfo.count
      );
    },
    getAvailableActions() {
      let actions = [
        {
          name: 'edit',
          label: 'Editar',
          icon: 'fas fa-edit color-primary-1',
          click: this.onEdit,
          visible: !this.disabled && this.editEnabled,
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-danger',
          click: this.onDelete,
          visible: !this.disabled && this.deleteEnabled,
        },
      ];

      return actions;
    },
    onAdd() {
      this.createMode = true;
      this.editMode = false;
      this.newLocation = this.getNewServicePlaceModel();
    },
    onEdit(dataItemIndex) {
      this.editMode = true;
      this.createMode = false;
      this.editIndex = dataItemIndex;
    },
    async onDelete(dataItemIndex) {
      var self = this;
      await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea eliminar el lugar de servicio seleccionado?',
        'Sí',
        'No'
      ).then(async (answer) => {
        if (answer) {
          if (this.immediateSave) {
            deleteServicePlace(
              self.sortedLocations[dataItemIndex].specialistLocationId
            )
              .then(() => {
                self.deleteFromModel(dataItemIndex, self);
                self.emitLocationChange();
              })
              .catch((error) => {
                self.ShowToast('Error', error.response.data.message, 'error');
              });
          } else {
            self.deleteFromModel(dataItemIndex, self);
            self.emitLocationChange();
          }
        }
      });
    },
    deleteFromModel(index, self) {
      self.sortedLocations.splice(index, 1);
    },
    emitLocationChange() {
      this.clearEditCreate();

      //MMR: this.sortedLocations contiene la lista actualizada.
      this.specialistData.locations = this.sortedLocations;
      this.$emit('on-location-change', this.specialistData);
    },
    async onLocationAdded() {
      const { data } = await _getMunicipalityNameByLocationFiscalSpecialist(
        this.newLocation.locationFiscalYearId
      );
      this.newLocation.municipalityName = data;
      this.locations.push(this.newLocation);
      this.emitLocationChange();
      this.clearEditCreate();
    },
    clearEditCreate() {
      this.createMode = false;
      this.editMode = false;
    },
    getNewServicePlaceModel() {
      let newLocation = {
        specialistLocationId: null,
        specialistId: this.specialistId,
        locationFiscalYearId: 0,
        locationTypeId: 0,
        canAssignSpecialistFlag: false,
        selfAssign: false,
        disciplines: [],
        fiscalYearId: this.specialist.fiscalYearId,
        companyId: this.specialist.companyId,
      };
      return newLocation;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';

.serviceplace-information-list .cell {
  display: table-cell !important;
  vertical-align: middle;
  margin-left: 2.2rem;
}

.serviceplace-information-list .cell span {
  color: map-get($colors, 'gray-6');
  font-size: map-get($fontsSize, 'font-14');
}

.serviceplace-information-list .cell p {
  color: map-get($colors, 'black');
  font-size: map-get($fontsSize, 'font-16');
  margin-bottom: 0;
}

.serviceplace-information-list .cell p:last-child {
  font-weight: 500;
}

.serviceplace-information-list .cell {
  margin-top: auto;
  margin-bottom: auto;
}

.serviceplace-information-list .cell.sd {
  margin-top: 4.5px;
}

.serviceplace-information-list .cell.actions {
  text-align: right;
}
.dot {
  background-color: #ff7058;
  box-shadow: 0px 3px 6px #00000029;
  margin: 2px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  border-color: #fff;
  display: inline-block;
}
.is-big-icon {
  margin-left: 3px;
}
.color-white {
  color: white;
}

@media (max-width: 768px) {
  .serviceplace-information-list .cell.actions {
    text-align: left !important;
  }

  .serviceplace-information-list .cell p:last-child {
    margin-top: 0;
    margin-bottom: 10px;
  }
}

@media (max-width: 992px) {
  .serviceplace-information-list .cell.hidden {
    display: none !important;
  }
}

@media (min-width: 992px) and (max-width: 1145px) {
  .serviceplace-information-list .cell.hidden {
    display: none !important;
  }
}
</style>
